import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useWalletSessionMaybe } from '../../context/WalletSessionContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { requestProgressiveAuth } from '../../util/authIntent';

import css from './SimpleAppExperience.module.css';

/**
 * Soft gate: never blocks. Prompts guest to sync via email or wallet.
 * Implements browse-free · sync-with-identity philosophy.
 */
const IdentitySyncBar = ({ syncStatus, className, compact = false }) => {
  const history = useHistory();
  const location = useLocation();
  const routes = useRouteConfiguration();
  const wallet = useWalletSessionMaybe();
  const isGuest = !wallet || wallet.isGuest;
  const authMode = wallet?.authMode || 'none';

  if (!isGuest && syncStatus === 'synced') {
    return (
      <p className={className || css.promise} data-testid="identity-sync-synced">
        {authMode === 'web3'
          ? 'Synced to your wallet session — available on devices with this wallet.'
          : 'Synced to your account — available on other devices.'}
      </p>
    );
  }

  if (!isGuest && syncStatus === 'hydrating') {
    return (
      <p className={className || css.promise} data-testid="identity-sync-hydrating">
        Syncing requests…
      </p>
    );
  }

  if (!isGuest && syncStatus === 'error') {
    return (
      <p className={className || css.promise} data-testid="identity-sync-error">
        Sync paused — check connection and retry from Activity.
      </p>
    );
  }

  if (!isGuest) {
    return null;
  }

  const openSync = () => {
    requestProgressiveAuth({
      history,
      routes,
      location,
      intent: 'syncJobs',
    });
  };

  if (compact) {
    return (
      <p className={className || css.promise} data-testid="identity-sync-guest">
        Saved on this device only.{' '}
        <button type="button" className={css.secondaryLink} onClick={openSync}>
          Sign in or connect wallet to sync
        </button>
      </p>
    );
  }

  return (
    <div className={className || css.coldStart} data-testid="identity-sync-bar">
      <p className={css.promise}>
        <strong>Saved on this device.</strong> Browse and requests stay login-free. Sync only if you
        want history on another phone.
      </p>
      <div className={css.actionRow}>
        <button
          type="button"
          className={css.secondaryBtn}
          onClick={openSync}
          data-testid="identity-sync-cta"
        >
          Sign in or connect wallet to sync
        </button>
      </div>
    </div>
  );
};

export default IdentitySyncBar;
